import { memo, useCallback } from 'react'
import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons'
import { useTranslation } from 'react-i18next'
import * as Select from '@radix-ui/react-select'
import { COUNTRIES } from './regionData'

type CountrySelectorProps = {
  country: string | undefined
  onSelectCountry: (country: string) => void
}

const CountrySelectorComponent = ({
  country,
  onSelectCountry,
}: CountrySelectorProps) => {
  const { t } = useTranslation()

  const handleValueChange = useCallback(
    (value: string) => {
      onSelectCountry(value)
    },
    [onSelectCountry]
  )

  return (
    <Select.Root
      value={country}
      onValueChange={handleValueChange}
      data-testid="form-countryselector"
    >
      <Select.Trigger
        type="button"
        className="text-pebble dark:text-snow py-2 h-full w-full inline-flex items-end gap-[5px] min-w-[150px] justify-between data-[placeholder]:text-gray-400 dark:data-[placeholder]:text-gray-400 outline-none border-b-rock border-b-2"
        data-testid="form-countryselector"
      >
        <div className="relative top-[4px] flex w-full justify-between">
          <Select.Value
            placeholder={t('countrySelector.placeholder')}
            data-testid="form-countryselector"
          />
        </div>
        <Select.Icon className="" data-testid="form-countryselector">
          <ChevronDownIcon />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          className="bg-white dark:bg-pebble overflow-hidden border border-pebble dark:border-white"
          data-testid="form-countryselector"
        >
          <Select.ScrollUpButton
            className="p-2.5"
            data-testid="form-countryselector"
          >
            <ChevronUpIcon />
          </Select.ScrollUpButton>
          <Select.Viewport data-testid="form-countryselector">
            {COUNTRIES.map(({ code, name }) => {
              return (
                <Select.Item
                  key={code}
                  value={code}
                  className="hover:bg-rock min-w-[150px] justify-between p-2 cursor-pointer"
                  data-testid={`country-selector-item-${code}`}
                >
                  <Select.ItemText
                    data-testid={`country-selector-item-text-${code}`}
                  >
                    {name}
                  </Select.ItemText>
                </Select.Item>
              )
            })}
          </Select.Viewport>
          <Select.ScrollDownButton
            className="p-2.5"
            data-testid="form-countryselector"
          >
            <ChevronDownIcon />
          </Select.ScrollDownButton>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  )
}

export const CountrySelector = memo(CountrySelectorComponent)
